import React, { useMemo, useCallback } from "react";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "react-select";
import useProjectEditAccess from "@/services/context/project-process/use-project-edit-access";
import useProjectFilePlanContext from "../context/use-project-file-plan-context.ts";
import Card from "../../../../../../../components/card";
import ToggleButton from "../../../../../../../components/buttons/button-toggle";
import useProjectOrganisationContext
    from "../../../../../context/project-organisation/use-project-organisation-context.ts";
import useProjectContext from "../../../../../../../services/context/project/use-project-context.ts";
import useProjectIfcFiles from "../../../../measures/hooks/use-project-ifc-files.ts";
import FileType from "../../../../../../../models/enums/file-type.ts";

type FileSelectorIem = { fileId: string, fileName: string, fileType: FileType };

const CardRight: React.FC = () => {
    const { canEdit } = useProjectEditAccess();
    const { getAllPlanFilesData, getAll3dFilesData } = useProjectOrganisationContext();
    const { project } = useProjectContext();
    const { ifcFiles } = useProjectIfcFiles(project.id);
    const { isOverlayActive, fileDataRight: fileData, fileLeft, fileRight, isDrawingRight: isDrawing, setIsDrawingRight: setIsDrawing, setFileIdRight, dispatchLeft, dispatchRight: dispatch } = useProjectFilePlanContext();

    const itemsSource = useMemo(() => {
        const files2d: FileSelectorIem[] = getAllPlanFilesData()
            .filter(f => f.fileId !== fileLeft?.id)
            .map(f => ({
                fileId: f.fileId,
                fileName: f.fileName,
                fileType: FileType.File2D
            }));

        const files3d: FileSelectorIem[] = getAll3dFilesData()
            .filter(f => ifcFiles.find(x => x.name.toLowerCase() === f.name.toLowerCase()))
            .map(f => ({
                fileId: f.id,
                fileName: f.name,
                fileType: FileType.File3D
            }));

        const merged = [
            ...files2d,
            ...files3d
        ];

        return merged.sort((a, b) => {
            if (a.fileName.toLowerCase().endsWith(".pdf")) return -1;
            if (b.fileName.toLowerCase().endsWith(".pdf")) return 1;

            return a.fileName.localeCompare(b.fileName, "fr", { sensitivity: "base" });
        })
    }, [fileLeft?.id, ifcFiles, getAllPlanFilesData, getAll3dFilesData]);

    const sortedOrigins = useMemo(() => {
        if (!fileData.origins) return [];

        return [...fileData.origins].sort((a, b) => a.name.localeCompare(b.name, "fr", { sensitivity: "base" }));
    }, [fileData.origins]);

    const selectedFile = useMemo(() => {
        if (!fileRight) return null;

        return itemsSource.find(f => f.fileId === fileRight.id) ?? null;
    }, [itemsSource, fileRight]);

    const handleSelectionFileChanged = useCallback((selectedItem: FileSelectorIem | null) => {
        if (!selectedItem) return;

        if (selectedItem.fileId === fileRight?.id)
            return;

        setFileIdRight(selectedItem.fileId);

        dispatchLeft({
            type: 'set-relative-origin',
            originId: undefined 
        });
    }, [fileRight?.id, setFileIdRight, dispatchLeft]);
    
    if(!isOverlayActive)
        return null;
    
    return (
        <div className='absolute top-4 right-4 z-12'>
            <Card additionalClasses='select-none w-96'>
                <div className='flex flex-col gap-y-2 px-4 py-2'>
                    <div className='grid grid-cols gap-2 items-center'>
                        <div className='self-start pt-1 text-sm'>Choisissez un plan ou une 3D</div>
                        <div className='text-sm flex flex-row items-center flex-1 pt-1'>
                            <Select isDisabled={!canEdit} className='w-full'
                                    placeholder='Sélectionnez un plan'
                                    options={itemsSource} value={selectedFile}
                                    getOptionValue={f => f.fileId}
                                    getOptionLabel={(selectedItem: FileSelectorIem) => selectedItem.fileName}
                                    onChange={handleSelectionFileChanged}
                                    classNames={{
                                        control: () => "!min-h-0 !rounded-md !border-gray-200",
                                        menu: () => "text-sm",
                                        menuList: () => "text-sm",
                                        singleValue: () => "text-sm",
                                        placeholder: () => "text-sm",
                                        indicatorsContainer: () => "-my-1",
                                    }}
                            />
                        </div>
                        <div className={fileRight ? 'visible' : 'hidden'}>
                            <div className='text-sm flex flex-row items-center flex-1 pt-1'>Créez une origine</div>
                            <div className="flex flex-col gap-2 border-1 p-2 border-gray-300 text-sm">
                                {sortedOrigins.map(o =>
                                    <label key={o.id} className="flex items-center gap-2 cursor-pointer">
                                        <input disabled={!canEdit}
                                            type="radio"
                                            name="origin-right"
                                            value={o.id}
                                            checked={o.isActive}
                                            onChange={() => {
                                                dispatch({
                                                    type: "change-origin",
                                                    origin: o
                                                });

                                                dispatchLeft({
                                                    type: "set-relative-origin",
                                                    originId: o.id
                                                });
                                            }}
                                        />
                                        <span style={{color: o.color}}>{o.name}</span>

                                        {/* <Button variant='ghost' size='sm' disabled={o.isActive} onClick={() => dispatch({
                                                    type: "delete-origin",
                                                    originId: o.id
                                                })}>
                                                    <FontAwesomeIcon icon={faTrash} className='text-red-500' />
                                                </Button> */}
                                    </label>
                                )}
                                <ToggleButton className='mr-auto' variant='ghost' size='sm' toggled={isDrawing} setIsToggled={setIsDrawing} disabled={!canEdit || (isDrawing)}>
                                    <FontAwesomeIcon icon={faPlus} className="text-green-500" />
                                </ToggleButton>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    )
}

export default CardRight;